import React, { useEffect, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import {
  BooleanField,
  Button,
  EditButton,
  FunctionField,
  ListControllerResult,
  ReferenceField,
  ShowButton,
  TextField,
  TextInput,
  WrapperField,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
} from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import { isEqual } from 'lodash';

import { usePermissions } from 'src/hooks';
import { CustomerDataPackageRecord, DataPackageApiFieldPolicies, DataPackageTemplateRecord } from 'src/types';
import { DEFAULT_DATA_PACKAGE_NAME } from 'src/utils/defaults/Constants';
import { CraftPageSection, CraftPageSectionProps } from 'src/components/CraftPageSection';
import CraftStandaloneList from 'src/components/CraftStandaloneList';
import { Center } from 'src/components/Center';
import BulkSoftDeleteButton from 'src/components/BulkSoftDeleteButton';
import { useAddDataPackageDialogContext } from '../../../customerDataPackages/AddDataPackageDialog';
import IntegerInput from 'src/inputs/IntegerInput';
import { getFieldComparisonDataMap } from 'src/utils/apiFieldsConfig';
import customerDataPackageTooltips from '../../../customerDataPackages/tooltips';

interface ListContextConsumerProps {
  setContext: React.Dispatch<React.SetStateAction<ListControllerResult | undefined>>;
}

const ListContextConsumer = ({ setContext }: ListContextConsumerProps) => {
  const listCtx = useListContext();

  useEffect(() => {
    setContext(listCtx);
  }, []);

  return null;
};

const DataPackagesSection = (props: Omit<CraftPageSectionProps, 'title'>) => {
  const record = useRecordContext();
  const addDataPkgDialogCtx = useAddDataPackageDialogContext();
  const { permissions } = usePermissions();
  const notify = useNotify();

  const [listCtx, setListCtx] = useState<ListControllerResult>();

  useEffect(() => {
    if (!listCtx) return;
    addDataPkgDialogCtx.setOnSuccess(listCtx.refetch);
  }, [listCtx]);

  const {
    data: dataPackageTemplatesData,
    isLoading: dataPackageTemplatesLoading,
    error: dataPackageTemplatesError,
  } = useGetList<DataPackageTemplateRecord>('data_package_templates', { meta: { noLimit: true } });

  if (dataPackageTemplatesError) {
    notify('There was a problem loading data package templates data. Please refresh to try again.', {
      type: 'error',
    });
  }

  const templateFieldsByTemplateID = useMemo(() => {
    if (!dataPackageTemplatesData) return {};
    return dataPackageTemplatesData.reduce((acc, template) => {
      acc[template.id] = getFieldComparisonDataMap(template.api_fields_xrefs);
      return acc;
    }, {} as Record<string, Record<string, Required<DataPackageApiFieldPolicies>>>);
  }, [dataPackageTemplatesData]);

  const filters = [
    <IntegerInput key="id" label="ID" source="id" alwaysOn />,
    <TextInput key="name" source="name" alwaysOn />,
  ];

  const Actions = permissions.has('customerDataPackage:edit') && (
    <Button label="Create" startIcon={<AddIcon />} onClick={addDataPkgDialogCtx.setOpen} />
  );

  return (
    <CraftPageSection title="Data Packages" loading={dataPackageTemplatesLoading} {...props}>
      <Paper sx={{ width: '100%', p: 2 }}>
        <CraftStandaloneList
          resource="customer_data_packages"
          filter={{ external_api_client_id: record.id }}
          filters={filters}
          actions={Actions}
          infoItems={customerDataPackageTooltips.customIndicator}
          dataGridProps={{
            empty: <Center m={4}>No data packages found</Center>,
            bulkActionButtons: permissions.has('customerDataPackage:delete') && <BulkSoftDeleteButton />,
          }}
        >
          <TextField label="ID" source="id" />
          <TextField label="Name" source="name" />
          <ReferenceField
            label="Template"
            source="data_package_template_id"
            reference="data_package_templates"
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
          <FunctionField
            label="Custom"
            render={(rec: CustomerDataPackageRecord) => {
              const packageCmpDataMap = getFieldComparisonDataMap(rec.api_fields_xrefs);
              const isCustom = !isEqual(packageCmpDataMap, templateFieldsByTemplateID[rec.data_package_template_id]);
              return <BooleanField record={{ isCustom }} source="isCustom" />;
            }}
          />
          <WrapperField label="Actions">
            <FunctionField
              render={(rec: CustomerDataPackageRecord) =>
                rec.name === DEFAULT_DATA_PACKAGE_NAME ? <ShowButton /> : <EditButton disabled={!!rec.deleted_at} />
              }
            />
          </WrapperField>

          <ListContextConsumer setContext={setListCtx} />
        </CraftStandaloneList>
      </Paper>
    </CraftPageSection>
  );
};

export default DataPackagesSection;
